import type { CuscomService } from '~/shared/api/mm-gateway/cuscom/CuscomService';
import type { IUserStoreApiService, IUserStoreMarketCustomer } from '../models';
import type { UserService } from '~/shared/api/magnit-id/user/UserSerivce';
import type { IUserMM } from '~/entities/user/models/IUserMM';

import { useLocalStorageNoChanges } from '#shared/helpers/useLocalStorageNoChanges';
import { ELocalStorageKeys } from '~/features/store/ELocalStorageKeys';
import { UserMM } from '~/entities/user/business/UserMM';
import { UserStoreConfig } from './UserStoreConfig';
import { mapUser } from '../mappers/UserMapper';

const emptyUser = new UserMM({
  id: '',
  phone: '',
  email: '',
  birthDate: '',
  firstName: '',
  sex: '',
});

export class UserStoreController {
  private user = useLocalStorageNoChanges(ELocalStorageKeys.MM__USER_STORE__USER, emptyUser);

  private hasAdultChecked = useLocalStorageNoChanges(
    ELocalStorageKeys.MM__USER_STORE__HAS_ADULT_CHECKED,
    false
  );

  private _isAdult = useLocalStorageNoChanges(ELocalStorageKeys.MM__USER_STORE__IS_ADULT, false);
  public marketCustomer = useLocalStorageNoChanges<IUserStoreMarketCustomer>(
    ELocalStorageKeys.MM__USER_STORE__MARKET_CUSTOMER,
    UserStoreConfig.MARKET_CUSTOMER_DEFAULT()
  );

  public isUserDataFetched = ref(false);

  private userService: UserService;
  private cuscomService: CuscomService;
  private apiService: IUserStoreApiService;

  constructor(
    userService: UserService,
    cuscomService: CuscomService,
    apiService: IUserStoreApiService
  ) {
    this.userService = userService;
    this.cuscomService = cuscomService;
    this.apiService = apiService;
  }

  public setUser(user: UserMM) {
    this.user.value = user;
  }

  public getUser(): UserMM {
    return this.user.value;
  }

  public async clearUser() {
    this.resetAdultChecker();
    this.user.value = emptyUser;
    this.marketCustomer.value = UserStoreConfig.MARKET_CUSTOMER_DEFAULT();
    this.isUserDataFetched.value = false;
  }

  public resetAdultChecker() {
    this.hasAdultChecked.value = false;
    this._isAdult.value = false;
  }

  public confirmAdult(isAdult: boolean) {
    this.hasAdultChecked.value = true;
    this._isAdult.value = isAdult;
  }

  public get isAdultChecked() {
    return this.hasAdultChecked.value;
  }

  public get isAdult() {
    return this._isAdult.value;
  }

  public async fetchUser() {
    const [user, marketCustomer] = await Promise.all([
      this.userService.getProfile(),
      this.apiService.getMarketCustomer(),
    ]);

    const mappedUser = mapUser(user);
    this.setUser(mappedUser);

    this.marketCustomer.value = marketCustomer;

    this.isUserDataFetched.value = true;
  }

  public async registerProfile(profile: IUserMM, magnitIdCode: string) {
    const params = {
      firstName: profile.firstName,
      birthDate: profile.birthDate,
      email: profile.email,
      magnitIdCode,
    };

    if (!params.birthDate) {
      delete params.birthDate;
    }

    const response = await this.userService.registerProfile(params);
    return response;
  }

  public async updateProfile(profile: IUserMM) {
    if (!profile.birthDate) {
      delete profile.birthDate;
    }

    const response = await this.userService.updateProfile(profile);
    this.setUser(mapUser(response));
    return response;
  }

  public async setNotifications() {
    await this.cuscomService.acceptNotifications();
  }

  public get isRequiredFieldsFilled() {
    return UserStoreConfig.REQUIRED_FIELDS.every(
      (field) => !!this.user.value[field as keyof UserMM]
    );
  }
}
